/**
 * Template Library Component
 * ===========================
 * 
 * Displays pre-built event templates that users can quickly add to their calendar.
 */

import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { 
  Plus, 
  TrendingUp, 
  TrendingDown, 
  Minus,
  Calendar,
  Search,
  CheckCircle,
  Loader2,
} from 'lucide-react';

// =============================================================================
// TYPES
// =============================================================================

interface EventTemplate {
  id: string;
  event_type: string;
  event_name: string;
  description: string | null;
  typical_timing: string | null;
  typical_month_start: number | null;
  typical_month_end: number | null;
  typical_week_of_year: number | null;
  typical_impact_direction: 'up' | 'down' | 'mixed' | null;
  typical_impact_pct_low: number | null;
  typical_impact_pct_high: number | null;
  affected_channels: string[];
  affected_platforms: string[];
  category: string | null;
}

interface TemplatesResponse {
  success: boolean;
  templates: EventTemplate[];
  by_category: Record<string, EventTemplate[]>;
  categories: string[];
  count: number;
}

interface Props {
  onAddFromTemplate: () => void;
}

// =============================================================================
// API FUNCTIONS
// =============================================================================

async function fetchTemplates(): Promise<TemplatesResponse> {
  const res = await fetch('/api/admin/context-events/templates');
  if (!res.ok) throw new Error('Failed to fetch templates');
  return res.json();
}

async function createEventFromTemplate(template: EventTemplate): Promise<void> {
  // Calculate start date based on template timing
  const today = new Date();
  let startDate = today.toISOString().split('T')[0];
  let endDate: string | undefined;
  
  // If template has typical month, use that
  if (template.typical_month_start) {
    const year = today.getMonth() >= template.typical_month_start 
      ? today.getFullYear() + 1 
      : today.getFullYear();
    startDate = `${year}-${String(template.typical_month_start).padStart(2, '0')}-01`;
    
    if (template.typical_month_end) {
      const endMonth = template.typical_month_end;
      const daysInMonth = new Date(year, endMonth, 0).getDate();
      endDate = `${year}-${String(endMonth).padStart(2, '0')}-${daysInMonth}`;
    }
  }
  
  const res = await fetch('/api/admin/context-events', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      event_type: template.event_type,
      event_name: template.event_name,
      description: template.description,
      start_date: startDate,
      end_date: endDate,
      expected_impact_direction: template.typical_impact_direction,
      expected_impact_pct_low: template.typical_impact_pct_low,
      expected_impact_pct_high: template.typical_impact_pct_high,
      affected_channels: template.affected_channels,
      affected_platforms: template.affected_platforms,
      source: 'template',
    }),
  });
  
  if (!res.ok) throw new Error('Failed to create event from template');
}

// =============================================================================
// HELPER COMPONENTS
// =============================================================================

const ImpactBadge: React.FC<{ direction: string | null; low?: number | null; high?: number | null }> = ({ 
  direction, low, high 
}) => {
  if (!direction) return null;
  
  const colors = {
    up: 'bg-emerald-500/20 text-emerald-400 border-emerald-500/30',
    down: 'bg-rose-500/20 text-rose-400 border-rose-500/30',
    mixed: 'bg-amber-500/20 text-amber-400 border-amber-500/30',
  };
  
  const icons = {
    up: <TrendingUp className="w-3 h-3" />,
    down: <TrendingDown className="w-3 h-3" />,
    mixed: <Minus className="w-3 h-3" />,
  };
  
  const range = low !== null && high !== null 
    ? `${low > 0 ? '+' : ''}${low}% to ${high > 0 ? '+' : ''}${high}%`
    : direction;
  
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs border ${colors[direction as keyof typeof colors] || 'bg-gray-500/20 text-gray-400'}`}>
      {icons[direction as keyof typeof icons]}
      {range}
    </span>
  );
};

const TemplateCard: React.FC<{ 
  template: EventTemplate; 
  onAdd: (template: EventTemplate) => void;
  isAdding: boolean;
  wasAdded: boolean;
}> = ({ template, onAdd, isAdding, wasAdded }) => {
  return (
    <div className="bg-gray-800/50 border border-gray-700/50 rounded-lg p-4 hover:border-gray-600/50 transition-colors">
      <div className="flex items-start justify-between gap-4">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <ImpactBadge 
              direction={template.typical_impact_direction} 
              low={template.typical_impact_pct_low}
              high={template.typical_impact_pct_high}
            />
          </div>
          
          <h3 className="font-medium text-white">{template.event_name}</h3>
          
          {template.description && (
            <p className="text-sm text-gray-400 mt-1 line-clamp-2">{template.description}</p>
          )}
          
          <div className="flex items-center gap-4 mt-2 text-xs text-gray-500">
            {template.typical_timing && (
              <span className="flex items-center gap-1">
                <Calendar className="w-3 h-3" />
                {template.typical_timing}
              </span>
            )}
            
            {template.affected_platforms.length > 0 && (
              <span>{template.affected_platforms.join(', ')}</span>
            )}
          </div>
        </div>
        
        <button
          onClick={() => onAdd(template)}
          disabled={isAdding || wasAdded}
          className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
            wasAdded
              ? 'bg-emerald-500/20 text-emerald-400 cursor-default'
              : isAdding
                ? 'bg-gray-700 text-gray-400 cursor-wait'
                : 'bg-amber-500 hover:bg-amber-600 text-black'
          }`}
        >
          {wasAdded ? (
            <>
              <CheckCircle className="w-4 h-4" />
              Added
            </>
          ) : isAdding ? (
            <>
              <Loader2 className="w-4 h-4 animate-spin" />
              Adding...
            </>
          ) : (
            <>
              <Plus className="w-4 h-4" />
              Add
            </>
          )}
        </button>
      </div>
    </div>
  );
};

// =============================================================================
// MAIN COMPONENT
// =============================================================================

const TemplateLibrary: React.FC<Props> = ({ onAddFromTemplate }) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);
  const [addingTemplateId, setAddingTemplateId] = useState<string | null>(null);
  const [addedTemplates, setAddedTemplates] = useState<Set<string>>(new Set());
  
  const queryClient = useQueryClient();
  
  const { data, isLoading, error } = useQuery({
    queryKey: ['context-event-templates'],
    queryFn: fetchTemplates,
  });
  
  const addMutation = useMutation({
    mutationFn: createEventFromTemplate,
    onSuccess: (_, template) => {
      setAddedTemplates(prev => new Set(prev).add(template.id));
      queryClient.invalidateQueries({ queryKey: ['context-events'] });
      onAddFromTemplate();
    },
    onSettled: () => {
      setAddingTemplateId(null);
    },
  });
  
  const handleAdd = (template: EventTemplate) => {
    setAddingTemplateId(template.id);
    addMutation.mutate(template);
  };
  
  // Filter templates
  const filteredTemplates = data?.templates.filter(t => {
    if (selectedCategory && t.category !== selectedCategory) return false;
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      return t.event_name.toLowerCase().includes(query) ||
             t.description?.toLowerCase().includes(query) ||
             t.category?.toLowerCase().includes(query);
    }
    return true;
  }) || [];
  
  if (isLoading) {
    return (
      <div className="text-center py-12 text-gray-400">
        Loading templates...
      </div>
    );
  }
  
  if (error) {
    return (
      <div className="text-center py-12 text-rose-400">
        Failed to load templates
      </div>
    );
  }
  
  return (
    <div>
      {/* Filters */}
      <div className="flex items-center gap-4 mb-6">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search templates..."
            className="w-full pl-10 pr-4 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white placeholder-gray-500 focus:border-amber-500 focus:ring-1 focus:ring-amber-500 outline-none"
          />
        </div>
        
        <div className="flex items-center gap-2">
          <button
            onClick={() => setSelectedCategory(null)}
            className={`px-3 py-1.5 rounded-lg text-sm transition-colors ${
              !selectedCategory
                ? 'bg-amber-500/20 text-amber-400 border border-amber-500/30'
                : 'text-gray-400 hover:text-white hover:bg-gray-800'
            }`}
          >
            All
          </button>
          {data?.categories.map(cat => (
            <button
              key={cat}
              onClick={() => setSelectedCategory(cat)}
              className={`px-3 py-1.5 rounded-lg text-sm transition-colors ${
                selectedCategory === cat
                  ? 'bg-amber-500/20 text-amber-400 border border-amber-500/30'
                  : 'text-gray-400 hover:text-white hover:bg-gray-800'
              }`}
            >
              {cat}
            </button>
          ))}
        </div>
      </div>
      
      {/* Templates Grid */}
      {filteredTemplates.length === 0 ? (
        <div className="text-center py-12 text-gray-400">
          No templates found
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {filteredTemplates.map(template => (
            <TemplateCard
              key={template.id}
              template={template}
              onAdd={handleAdd}
              isAdding={addingTemplateId === template.id}
              wasAdded={addedTemplates.has(template.id)}
            />
          ))}
        </div>
      )}
    </div>
  );
};

export default TemplateLibrary;
